import { createRoute } from '@tanstack/react-router';
import { Route as authedRoute } from '../_authed';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton as MuiIconButton,
  Tooltip,
} from '@mui/material';
import KundeForm from '@/features/filters/KundeForm';
import { isAxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listMailboxes, listFolders, type Folder } from '@/features/mail';
import {
  getFilters,
  saveFilters,
  editExistingRule,
  deleteExistingRule,
  type ProxyRule,
  type ExistingRule,
} from '@/features/filters';

const FIELDS = ['from', 'to', 'subject', 'cc'];

function FiltersPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const [mailbox, setMailbox] = useState<string | null>(search.prefillMailbox ?? null);
  const [rules, setRules] = useState<ProxyRule[]>([]);
  const [prefillApplied, setPrefillApplied] = useState(false);
  const [mode, setMode] = useState<'kunde' | 'technical'>('kunde');
  const [existingQuery, setExistingQuery] = useState('');
  // Vorher war die Liste hart auf 200 Zeilen abgeschnitten — bei ~2000 Regeln
  // blieb der Rest unerreichbar. Jetzt geblaettert.
  const [existingPage, setExistingPage] = useState(0);
  const [existingPerPage, setExistingPerPage] = useState(100);
  const [editingExisting, setEditingExisting] = useState<ExistingRule | null>(null);
  const [snack, setSnack] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);
  const qc = useQueryClient();
  const invalidateFilters = () => qc.invalidateQueries({ queryKey: ['filters', mailbox] });

  const mailboxesQ = useQuery({ queryKey: ['mailboxes'], queryFn: listMailboxes });
  useEffect(() => {
    if (!mailbox && mailboxesQ.data?.length) setMailbox(mailboxesQ.data[0].address);
  }, [mailboxesQ.data, mailbox]);

  const foldersQ = useQuery({
    queryKey: ['folders', mailbox],
    queryFn: () => listFolders(mailbox as string),
    enabled: !!mailbox,
  });
  const filtersQ = useQuery({
    queryKey: ['filters', mailbox],
    queryFn: () => getFilters(mailbox as string),
    enabled: !!mailbox,
  });
  useEffect(() => {
    if (!filtersQ.data) return;
    const base = filtersQ.data.rules.map((r) => ({ ...r }));
    if (search.prefillFrom && !prefillApplied) {
      const exists = base.some((r) => r.field === 'from' && r.contains === search.prefillFrom);
      if (!exists) {
        base.push({
          name: search.prefillName || search.prefillFrom,
          field: 'from',
          contains: search.prefillFrom,
          fileinto: null,
          forward: null,
          stop: false,
        });
      }
      setPrefillApplied(true);
    }
    setRules(base);
  }, [filtersQ.data, search, prefillApplied]);

  const filteredExisting = useMemo(() => {
    const q = existingQuery.trim().toLowerCase();
    // Neue Regeln haengt Sieve hinten an — umgedreht steht die zuletzt
    // angelegte oben, und man sieht sofort, ob das Anlegen geklappt hat.
    const all = (filtersQ.data?.existing ?? []).slice().reverse();
    if (!q) return all;
    return all.filter((e) => e.name.toLowerCase().includes(q) || e.summary.toLowerCase().includes(q));
  }, [filtersQ.data, existingQuery]);

  // Suche oder Postfachwechsel -> zurueck auf Seite 1, sonst steht man auf
  // einer Seite, die es im neuen Ergebnis gar nicht mehr gibt.
  useEffect(() => {
    setExistingPage(0);
  }, [existingQuery, mailbox]);

  const saveMut = useMutation({
    mutationFn: () => saveFilters(mailbox as string, rules),
    onSuccess: (d) => {
      setRules(d.rules.map((r) => ({ ...r })));
      setSnack({ msg: t('mail.filters.saved'), sev: 'success' });
    },
    onError: (e) =>
      setSnack({
        msg: isAxiosError(e) ? e.response?.data?.detail ?? t('mail.filters.error') : t('mail.filters.error'),
        sev: 'error',
      }),
  });

  const deleteExistingMut = useMutation({
    mutationFn: (name: string) => deleteExistingRule(mailbox as string, name),
    onSuccess: () => {
      invalidateFilters();
      setSnack({ msg: t('mail.filters.saved'), sev: 'success' });
    },
    onError: () => setSnack({ msg: t('mail.filters.error'), sev: 'error' }),
  });

  const folderOpts = foldersQ.data ?? [];
  const set = (i: number, patch: Partial<ProxyRule>) =>
    setRules((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRule = () =>
    setRules((prev) => [
      ...prev,
      { name: `Regel ${prev.length + 1}`, field: 'from', contains: '', fileinto: null, forward: null, stop: false },
    ]);
  const valid = useMemo(
    () => rules.every((r) => r.name.trim() && r.contains.trim() && (r.fileinto || r.forward)),
    [rules]
  );

  const switcher = (
    <FormControl size="small" sx={{ minWidth: 240 }}>
      <InputLabel id="fl-mb">{t('mail.mailbox.mailboxLabel')}</InputLabel>
      <Select
        labelId="fl-mb"
        label={t('mail.mailbox.mailboxLabel')}
        value={mailbox ?? ''}
        onChange={(e) => setMailbox(e.target.value)}
      >
        {mailboxesQ.data?.map((m) => (
          <MenuItem key={m.address} value={m.address}>
            {m.display_name} — {m.address}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Typography variant="h2" component="h1">{t('mail.filters.title')}</Typography>
        {switcher}
      </Stack>

      {filtersQ.isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
      {filtersQ.isError && (
        <Alert severity={isAxiosError(filtersQ.error) && filtersQ.error.response?.status === 403 ? 'warning' : 'error'}>
          {isAxiosError(filtersQ.error) && filtersQ.error.response?.status === 403 ? t('mail.filters.noAccess') : t('mail.filters.error')}
        </Alert>
      )}

      {filtersQ.data && mailbox && (
        <Tabs value={mode} onChange={(_, v) => setMode(v)} sx={{ mb: 2 }}>
          <Tab value="kunde" label={t('mail.filters.modeKunde')} />
          <Tab value="technical" label={t('mail.filters.modeTechnical')} />
        </Tabs>
      )}

      {filtersQ.data && mailbox && mode === 'kunde' && (
        <Box sx={{ mb: 2 }}>
          <KundeForm
            address={mailbox}
            onCreated={(p) => {
              setSnack({ msg: t('mail.kunde.created', { alias: p.alias }), sev: 'success' });
              qc.invalidateQueries({ queryKey: ['filters', mailbox] });
            }}
            onError={() => setSnack({ msg: t('mail.filters.saveError'), sev: 'error' })}
          />
        </Box>
      )}

      {filtersQ.data && mode === 'technical' && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h3">{t('mail.filters.myRules')}</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addRule}>{t('mail.filters.addRule')}</Button>
            </Stack>
            {rules.length === 0 && <Typography variant="body2" color="textSecondary">{t('mail.filters.none')}</Typography>}
            {rules.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('mail.filters.name')}</TableCell>
                    <TableCell>{t('mail.filters.field')}</TableCell>
                    <TableCell>{t('mail.filters.contains')}</TableCell>
                    <TableCell>{t('mail.filters.toFolder')}</TableCell>
                    <TableCell>{t('mail.filters.forward')}</TableCell>
                    <TableCell align="center">{t('mail.filters.stop')}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rules.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell><TextField variant="standard" value={r.name} onChange={(e) => set(i, { name: e.target.value })} /></TableCell>
                      <TableCell>
                        <Select variant="standard" value={r.field} onChange={(e) => set(i, { field: e.target.value })}>
                          {FIELDS.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                        </Select>
                      </TableCell>
                      <TableCell><TextField variant="standard" value={r.contains} onChange={(e) => set(i, { contains: e.target.value })} placeholder="…" /></TableCell>
                      <TableCell>
                        <Select variant="standard" displayEmpty value={r.fileinto ?? ''} onChange={(e) => set(i, { fileinto: e.target.value || null })} sx={{ minWidth: 120 }}>
                          <MenuItem value=""><em>—</em></MenuItem>
                          {folderOpts.map((f) => <MenuItem key={f.name} value={f.name}>{f.label}</MenuItem>)}
                        </Select>
                      </TableCell>
                      <TableCell><TextField variant="standard" value={r.forward ?? ''} onChange={(e) => set(i, { forward: e.target.value || null })} placeholder="email@…" /></TableCell>
                      <TableCell align="center"><Checkbox checked={r.stop} onChange={(e) => set(i, { stop: e.target.checked })} /></TableCell>
                      <TableCell><IconButton size="small" color="error" onClick={() => setRules((prev) => prev.filter((_, idx) => idx !== i))}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="center">
              <Button variant="contained" disabled={!valid || saveMut.isPending} onClick={() => saveMut.mutate()}>
                {saveMut.isPending ? t('mail.filters.saving') : t('mail.filters.save')}
              </Button>
              {!valid && <Typography variant="caption" color="error">{t('mail.filters.invalid')}</Typography>}
            </Stack>
          </Paper>
        </>
      )}

      {/* Die Regelliste haengt nicht am Reiter oben — sie ist immer dieselbe. */}
      {filtersQ.data && (
        <>
          <Accordion variant="outlined" disableGutters defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{t('mail.filters.existing')} ({filtersQ.data.total_existing})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                {t('mail.filters.existingHint')}
              </Typography>
              <TextField
                size="small"
                fullWidth
                placeholder={t('mail.filters.searchExisting')}
                value={existingQuery}
                onChange={(e) => setExistingQuery(e.target.value)}
                sx={{ mb: 1 }}
              />
              <Table size="small">
                <TableBody>
                  {filteredExisting
                    .slice(existingPage * existingPerPage, existingPage * existingPerPage + existingPerPage)
                    .map((e, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ width: 240 }}>{e.name}</TableCell>
                      <TableCell><Typography variant="caption" color="textSecondary">{e.summary}</Typography></TableCell>
                      <TableCell align="right" sx={{ width: 90 }}>
                        {e.simple && (
                          <>
                            <Tooltip title={t('mail.filters.edit')}>
                              <MuiIconButton size="small" onClick={() => setEditingExisting(e)}><EditIcon fontSize="small" /></MuiIconButton>
                            </Tooltip>
                            <Tooltip title={t('mail.filters.delete')}>
                              <MuiIconButton size="small" color="error" onClick={() => { if (confirm(t('mail.filters.confirmDelete', { name: e.name }))) deleteExistingMut.mutate(e.name); }}><DeleteIcon fontSize="small" /></MuiIconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={filteredExisting.length}
                page={existingPage}
                onPageChange={(_, p) => setExistingPage(p)}
                rowsPerPage={existingPerPage}
                onRowsPerPageChange={(e) => {
                  setExistingPerPage(parseInt(e.target.value, 10));
                  setExistingPage(0);
                }}
                rowsPerPageOptions={[50, 100, 200, 500]}
                labelRowsPerPage={t('mail.filters.rowsPerPage')}
              />
              {existingQuery.trim() !== '' && (
                <Typography variant="caption" color="textSecondary">
                  {t('mail.filters.searchMatches', {
                    matches: filteredExisting.length,
                    total: filtersQ.data.total_existing,
                  })}
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </>
      )}

      {editingExisting && mailbox && (
        <EditExistingDialog
          address={mailbox}
          rule={editingExisting}
          folders={folderOpts}
          onClose={() => setEditingExisting(null)}
          onDone={() => { setEditingExisting(null); invalidateFilters(); setSnack({ msg: t('mail.filters.saved'), sev: 'success' }); }}
          onError={() => setSnack({ msg: t('mail.filters.error'), sev: 'error' })}
        />
      )}

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {snack ? <Alert severity={snack.sev} onClose={() => setSnack(null)}>{snack.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}

interface FilterSearch {
  prefillFrom?: string;
  prefillName?: string;
  prefillMailbox?: string;
}

function EditExistingDialog({
  address,
  rule,
  folders,
  onClose,
  onDone,
  onError,
}: {
  address: string;
  rule: ExistingRule;
  folders: Folder[];
  onClose: () => void;
  onDone: () => void;
  onError: () => void;
}) {
  const { t } = useTranslation();
  const [field, setField] = useState(rule.field || 'from');
  const [contains, setContains] = useState(rule.contains || '');
  const [fileinto, setFileinto] = useState<string | null>(rule.fileinto);
  const [forward, setForward] = useState<string | null>(rule.forward);
  const [stop, setStop] = useState(rule.stop);
  const mut = useMutation({
    mutationFn: () =>
      editExistingRule(address, rule.name, { field, contains, fileinto, forward, stop }),
    onSuccess: onDone,
    onError,
  });
  const ok = contains.trim() && (fileinto || forward);
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('mail.filters.edit')} — {rule.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="ee-field">{t('mail.filters.field')}</InputLabel>
            <Select labelId="ee-field" label={t('mail.filters.field')} value={field} onChange={(e) => setField(e.target.value)}>
              {FIELDS.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" fullWidth label={t('mail.filters.contains')} value={contains} onChange={(e) => setContains(e.target.value)} />
          <FormControl size="small" fullWidth>
            <InputLabel id="ee-folder">{t('mail.filters.toFolder')}</InputLabel>
            <Select labelId="ee-folder" label={t('mail.filters.toFolder')} displayEmpty value={fileinto ?? ''} onChange={(e) => setFileinto(e.target.value || null)}>
              <MenuItem value=""><em>—</em></MenuItem>
              {folders.map((f) => <MenuItem key={f.name} value={f.name}>{f.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" fullWidth label={t('mail.filters.forward')} value={forward ?? ''} onChange={(e) => setForward(e.target.value || null)} placeholder="email@…" />
          <FormControlLabel control={<Checkbox checked={stop} onChange={(e) => setStop(e.target.checked)} />} label={t('mail.filters.stop')} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('mail.compose.cancel')}</Button>
        <Button variant="contained" disabled={!ok || mut.isPending} onClick={() => mut.mutate()}>{t('mail.filters.save')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export const Route = createRoute({
  getParentRoute: () => authedRoute,
  path: 'filters',
  validateSearch: (s: Record<string, unknown>): FilterSearch => ({
    prefillFrom: typeof s.prefillFrom === 'string' ? s.prefillFrom : undefined,
    prefillName: typeof s.prefillName === 'string' ? s.prefillName : undefined,
    prefillMailbox: typeof s.prefillMailbox === 'string' ? s.prefillMailbox : undefined,
  }),
  component: FiltersPage,
});
